import { cloneDeep } from 'lodash'
import { getPopularityRadiusValue } from '../../../enums/popularity.enum'
import { ConstellationDataInterface } from '../../../interface/analytics/mirror/ConstellationDataInterface.interface'
import { AuthorMirrorAuthorInterface } from '../../../interface/report/author-mirror-author.interface'

const authorMirrorConstellationPositionUtil = ({
  center,
  radiusBaseCircle,
  authorsOutput,
}: {
  center: { x: number; y: number }
  radiusBaseCircle: number[]
  authorsOutput: AuthorMirrorAuthorInterface[]
}): {
  level1: ConstellationDataInterface<AuthorMirrorAuthorInterface>[]
  level2: ConstellationDataInterface<AuthorMirrorAuthorInterface>[]
  level3: ConstellationDataInterface<AuthorMirrorAuthorInterface>[]
  level4: ConstellationDataInterface<AuthorMirrorAuthorInterface>[]
  level5: ConstellationDataInterface<AuthorMirrorAuthorInterface>[]
} => {
  const angleA = (2 * Math.PI) / 3
  const angleDecayA = Math.PI / 2

  const level1: ConstellationDataInterface<AuthorMirrorAuthorInterface>[] = []
  const booksOutputDeep: AuthorMirrorAuthorInterface[] =
    cloneDeep(authorsOutput)

  for (let i = 0; i < 3; i += 1) {
    const mirror = booksOutputDeep[0]
    const angle = i * angleA + angleDecayA
    const x = center.x + radiusBaseCircle[0] * Math.cos(angle)
    const y = center.y + radiusBaseCircle[0] * Math.sin(angle)

    if (mirror) {
      level1.push({
        x,
        y,
        r: getPopularityRadiusValue(mirror?.popularity),
        angle,
        color: '#00beef',
        mirror,
        center,
      })
      booksOutputDeep.shift()
    }
  }

  const level2: ConstellationDataInterface<AuthorMirrorAuthorInterface>[] = []

  for (let i = 0; i < 3; i += 1) {
    const mirror = booksOutputDeep[0]
    const angle = i * angleA - angleDecayA
    const x = center.x + radiusBaseCircle[1] * Math.cos(angle)
    const y = center.y + radiusBaseCircle[1] * Math.sin(angle)

    if (mirror) {
      level2.push({
        x,
        y,
        r: getPopularityRadiusValue(mirror?.popularity),
        angle,
        color: '#00beef',
        mirror,
        center,
      })
      booksOutputDeep.shift()
    }
  }

  const level3: ConstellationDataInterface<AuthorMirrorAuthorInterface>[] = []

  const angleB = (2 * Math.PI) / 6
  for (let i = 0; i < 6; i += 1) {
    const mirror = booksOutputDeep[0]
    const angle = i * angleB
    const x = center.x + radiusBaseCircle[2] * Math.cos(angle)
    const y = center.y + radiusBaseCircle[2] * Math.sin(angle)

    if (mirror) {
      level3.push({
        x,
        y,
        r: getPopularityRadiusValue(mirror?.popularity),
        angle,
        color: '#00beef',
        mirror,
        center,
      })
      booksOutputDeep.shift()
    }
  }

  const level4: ConstellationDataInterface<AuthorMirrorAuthorInterface>[] = []
  for (let i = 0; i < 6; i += 1) {
    const mirror = booksOutputDeep[0]
    const angle = i * angleB + angleDecayA
    const x = center.x + radiusBaseCircle[3] * Math.cos(angle)
    const y = center.y + radiusBaseCircle[3] * Math.sin(angle)

    if (mirror) {
      level4.push({
        x,
        y,
        r: getPopularityRadiusValue(mirror?.popularity),
        angle,
        color: '#00beef',
        mirror,
        center,
      })
      booksOutputDeep.shift()
    }
  }

  const level5: ConstellationDataInterface<AuthorMirrorAuthorInterface>[] = []
  const angleC = Math.PI / 6
  for (let i = 0; i < 12; i += 1) {
    const mirror = booksOutputDeep[0]
    const angle = i * angleC + angleDecayA
    const x = center.x + radiusBaseCircle[4] * Math.cos(angle)
    const y = center.y + radiusBaseCircle[4] * Math.sin(angle)

    if (mirror) {
      level4.push({
        x,
        y,
        r: getPopularityRadiusValue(mirror?.popularity),
        angle,
        color: '#00beef',
        mirror,
        center,
      })
      booksOutputDeep.shift()
    }
  }

  level1.forEach(
    (l1: ConstellationDataInterface<AuthorMirrorAuthorInterface>) => {
      l1.edges = [
        {
          x: center.x,
          y: center.y,
          r: 50,
        },
      ]
      if (l1?.mirror?.edges?.length) {
        l1.mirror.edges.forEach((value: string) => {
          const levelSelect =
            (level1.some(
              (l1a: ConstellationDataInterface<AuthorMirrorAuthorInterface>) =>
                l1a.mirror?.author_id === value,
            ) &&
              level1) ||
            (level2.some(
              (l2: ConstellationDataInterface<AuthorMirrorAuthorInterface>) =>
                l2.mirror?.author_id === value,
            ) &&
              level2) ||
            (level3.some(
              (l3: ConstellationDataInterface<AuthorMirrorAuthorInterface>) =>
                l3.mirror?.author_id === value,
            ) &&
              level3) ||
            (level4.some(
              (l4: ConstellationDataInterface<AuthorMirrorAuthorInterface>) =>
                l4.mirror?.author_id === value,
            ) &&
              level4) ||
            (level5.some(
              (l5: ConstellationDataInterface<AuthorMirrorAuthorInterface>) =>
                l5.mirror?.author_id === value,
            ) &&
              level5) ||
            null
          if (levelSelect) {
            const node = levelSelect.find(
              (
                levelS: ConstellationDataInterface<AuthorMirrorAuthorInterface>,
              ) => levelS.mirror?.author_id === value,
            )
            l1.edges?.push({ x: node?.x, y: node?.y, r: node?.r })
          }
        })
      }
    },
  )

  level2.forEach(
    (l2: ConstellationDataInterface<AuthorMirrorAuthorInterface>) => {
      l2.edges = [
        {
          x: center.x,
          y: center.y,
          r: 50,
        },
      ]
      if (l2?.mirror?.edges?.length) {
        l2.mirror.edges.forEach((value: string) => {
          const levelSelect =
            (level1.some(
              (l1: ConstellationDataInterface<AuthorMirrorAuthorInterface>) =>
                l1.mirror?.author_id === value,
            ) &&
              level1) ||
            (level2.some(
              (l2a: ConstellationDataInterface<AuthorMirrorAuthorInterface>) =>
                l2a.mirror?.author_id === value,
            ) &&
              level1) ||
            (level3.some(
              (l3: ConstellationDataInterface<AuthorMirrorAuthorInterface>) =>
                l3.mirror?.author_id === value,
            ) &&
              level3) ||
            (level4.some(
              (l4: ConstellationDataInterface<AuthorMirrorAuthorInterface>) =>
                l4.mirror?.author_id === value,
            ) &&
              level4) ||
            (level5.some(
              (l5: ConstellationDataInterface<AuthorMirrorAuthorInterface>) =>
                l5.mirror?.author_id === value,
            ) &&
              level5) ||
            null
          if (levelSelect) {
            const node = levelSelect.find(
              (
                levelS: ConstellationDataInterface<AuthorMirrorAuthorInterface>,
              ) => levelS.mirror?.author_id === value,
            )
            l2.edges?.push({ x: node?.x, y: node?.y, r: node?.r })
          }
        })
      }
    },
  )

  level3.forEach(
    (l3: ConstellationDataInterface<AuthorMirrorAuthorInterface>) => {
      l3.edges = [
        {
          x: center.x,
          y: center.y,
          r: 50,
        },
      ]
      if (l3?.mirror?.edges?.length) {
        l3?.mirror?.edges.forEach((value: string) => {
          const levelSelect =
            (level1.some(
              (l1: ConstellationDataInterface<AuthorMirrorAuthorInterface>) =>
                l1.mirror?.author_id === value,
            ) &&
              level1) ||
            (level2.some(
              (l2: ConstellationDataInterface<AuthorMirrorAuthorInterface>) =>
                l2.mirror?.author_id === value,
            ) &&
              level2) ||
            (level3.some(
              (l3a: ConstellationDataInterface<AuthorMirrorAuthorInterface>) =>
                l3a.mirror?.author_id === value,
            ) &&
              level3) ||
            (level4.some(
              (l4: ConstellationDataInterface<AuthorMirrorAuthorInterface>) =>
                l4.mirror?.author_id === value,
            ) &&
              level4) ||
            (level5.some(
              (l5: ConstellationDataInterface<AuthorMirrorAuthorInterface>) =>
                l5.mirror?.author_id === value,
            ) &&
              level5) ||
            null
          if (levelSelect) {
            const node = levelSelect.find(
              (
                levelS: ConstellationDataInterface<AuthorMirrorAuthorInterface>,
              ) => levelS.mirror?.author_id === value,
            )
            l3.edges?.push({ x: node?.x, y: node?.y, r: node?.r })
          }
        })
      }
    },
  )

  level4.forEach(
    (l4: ConstellationDataInterface<AuthorMirrorAuthorInterface>) => {
      l4.edges = [
        {
          x: center.x,
          y: center.y,
          r: 50,
        },
      ]
      if (l4?.mirror?.edges?.length) {
        l4.mirror.edges.forEach((value: string) => {
          const levelSelect =
            (level1.some(
              (l1: ConstellationDataInterface<AuthorMirrorAuthorInterface>) =>
                l1.mirror?.author_id === value,
            ) &&
              level1) ||
            (level2.some(
              (l2: ConstellationDataInterface<AuthorMirrorAuthorInterface>) =>
                l2.mirror?.author_id === value,
            ) &&
              level2) ||
            (level3.some(
              (l3: ConstellationDataInterface<AuthorMirrorAuthorInterface>) =>
                l3.mirror?.author_id === value,
            ) &&
              level3) ||
            (level4.some(
              (l4a: ConstellationDataInterface<AuthorMirrorAuthorInterface>) =>
                l4a.mirror?.author_id === value,
            ) &&
              level4) ||
            (level5.some(
              (l5: ConstellationDataInterface<AuthorMirrorAuthorInterface>) =>
                l5.mirror?.author_id === value,
            ) &&
              level5) ||
            null
          if (levelSelect) {
            const node = levelSelect.find(
              (
                levelS: ConstellationDataInterface<AuthorMirrorAuthorInterface>,
              ) => levelS.mirror?.author_id === value,
            )
            l4.edges?.push({ x: node?.x, y: node?.y, r: node?.r })
          }
        })
      }
    },
  )

  level5.forEach(
    (l5: ConstellationDataInterface<AuthorMirrorAuthorInterface>) => {
      l5.edges = [
        {
          x: center.x,
          y: center.y,
          r: 50,
        },
      ]
      if (l5?.mirror?.edges?.length) {
        l5.mirror.edges.forEach((value: string) => {
          const levelSelect =
            (level1.some(
              (l1: ConstellationDataInterface<AuthorMirrorAuthorInterface>) =>
                l1.mirror?.author_id === value,
            ) &&
              level1) ||
            (level2.some(
              (l2: ConstellationDataInterface<AuthorMirrorAuthorInterface>) =>
                l2.mirror?.author_id === value,
            ) &&
              level2) ||
            (level3.some(
              (l3: ConstellationDataInterface<AuthorMirrorAuthorInterface>) =>
                l3.mirror?.author_id === value,
            ) &&
              level3) ||
            (level4.some(
              (l4: ConstellationDataInterface<AuthorMirrorAuthorInterface>) =>
                l4.mirror?.author_id === value,
            ) &&
              level4) ||
            (level5.some(
              (l5a: ConstellationDataInterface<AuthorMirrorAuthorInterface>) =>
                l5a.mirror?.author_id === value,
            ) &&
              level5) ||
            null
          if (levelSelect) {
            const node = levelSelect.find(
              (
                levelS: ConstellationDataInterface<AuthorMirrorAuthorInterface>,
              ) => levelS.mirror?.author_id === value,
            )
            l5.edges?.push({ x: node?.x, y: node?.y, r: node?.r })
          }
        })
      }
    },
  )

  return { level1, level2, level3, level4, level5 }
}

export default authorMirrorConstellationPositionUtil
